import {
  Box,
  Typography,
  Button,
  Divider,
  Link as MuiLink,
  Grid,
} from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import styled from "@emotion/styled";
import { StatusChip } from "../find-listings-search/components/StatusChip";
import { textFormatter } from "@shared/utils/text-formatter";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  ImageUnavailable,
  Skeleton,
  SubmitButton,
} from "@avenue-8/sidekick-ui";
import { Image } from "@avenue-8/sidekick-ui";
import { IListingDetailsWidget } from "./types/ListingDetailsWidget";
import { useWidgetsGetListingByMlsId } from "@cma/shared/queries/useWidgetsGetListingByMlsId";
import { useMemo, useState } from "react";
import { convertSinglePropertyToListing } from "./helpers/convert-single-property-to-listing";
import { Keyboard, Navigation, Pagination } from "swiper/modules";
import { ChatWidgetContainer } from "../styles";
import { ChatWidgetNavbar } from "../ChatWidgetNavbar";
import { getListingCurrentPrice } from "@cma/shared/helpers/listing-price-resolver";
import { ApiError } from "@shared/apis/sidekick/api-error-handler";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";

const SwiperImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  overflow: hidden;
`;

const PropertyInfoSection = styled(Grid)`
  border-radius: 0.5rem;
  border: solid 1px ${(p) => p.theme.colors.border2};
  padding: 1rem;
`;

const MlsInfoBox = styled(Box)`
  background-color: ${(p) => p.theme.colors.background2};
  padding: 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
`;

type ListingDetailsWidgetProps = {
  widget: IListingDetailsWidget;
};

export const ListingDetailsWidget = ({ widget }: ListingDetailsWidgetProps) => {
  const [apiError, setApiError] = useState<ApiError | null>(null);

  const listingQuery = useWidgetsGetListingByMlsId({
    mlsId: widget.listingReference?.mlsId || "",
    mlsSource: widget.listingReference?.mlsSource || "",
    onError: (error) => {
      setApiError(error);
    },
  });

  const { data, isLoading, refetch } = listingQuery;

  const listing = useMemo(() => {
    if (data) {
      return convertSinglePropertyToListing(data);
    }
    return widget.listing || null;
  }, [widget, data]);

  if (isLoading) {
    return (
      <Box>
        <Skeleton height={320} width={"100%"} variant="rectangular" />
        <Skeleton
          variant="text"
          height={48}
          width={"85%"}
          sx={{ m: 2, mb: 0 }}
        />
        <Box p={2}>
          <PropertyInfoSection container>
            <Skeleton variant="rounded" height={300} width={"100%"} />
          </PropertyInfoSection>
        </Box>
        <Skeleton
          variant="text"
          height={48}
          width={"35%"}
          sx={{ m: 2, mt: 0 }}
        />
      </Box>
    );
  }

  if (!listing || apiError) {
    const isNotFoundError = apiError?.status === 404;

    return (
      <ChatWidgetContainer $embedded={widget.embedded}>
        {!widget.embedded && (
          <ChatWidgetNavbar>
            <Typography variant="subtitle1">Property Details</Typography>
          </ChatWidgetNavbar>
        )}
        <Box py={4} px={2} textAlign={"center"}>
          {isNotFoundError ? (
            <>
              <Typography>
                <TravelExploreIcon fontSize="large" />
              </Typography>
              <Typography variant="h6">Listing not found</Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 1, mb: 2 }}
              >
                The property you are looking for could not be found in our
                database.
              </Typography>
              <MlsInfoBox>
                <Typography variant="caption">
                  <strong>Source:</strong> {widget.listingReference?.mlsSource}{" "}
                  #{widget.listingReference?.mlsId}
                </Typography>
              </MlsInfoBox>
              <Typography variant="body2">
                If you believe this is a mistake, please contact support.
              </Typography>
            </>
          ) : (
            <Box
              px={2}
              display={"flex"}
              flexDirection="column"
              alignItems="center"
              gap={1}
            >
              <BrokenImageIcon fontSize="large" />
              <Typography variant="h6">Ops... something went wrong</Typography>
              <Typography px={6} textAlign={"center"} mb={2}>
                Error loading property details. If the problem persists, please
                contact support.
              </Typography>
              <SubmitButton
                variant="outlined"
                size="small"
                onClick={() => refetch()}
                isLoading={listingQuery.isFetching}
              >
                Retry
              </SubmitButton>
            </Box>
          )}
        </Box>
      </ChatWidgetContainer>
    );
  }

  const {
    neighborhood,
    photos = [],
    addressLine1,
    addressLine2,
    city,
    state,
    zipCode,
    listPrice,
    soldPrice,
    bedroomCount,
    bathroomCount,
    numBedrooms,
    numBathrooms,
    internalSize,
    yearBuilt,
    propertyType,
    description,
    link,
    lotSize,
    parkingGarageSpaces,
    isLease,
    listingStatus,
  } = listing;

  const address = [addressLine1, addressLine2].filter((x) => x).join(", ");
  const fullAddress = `${address}, ${city}, ${state} ${zipCode}`;

  return (
    <ChatWidgetContainer $embedded={widget.embedded}>
      {!widget.embedded && (
        <ChatWidgetNavbar>
          <Typography variant="subtitle1">Property Details</Typography>
        </ChatWidgetNavbar>
      )}

      <Box pb={4}>
        <Swiper
          modules={[Navigation, Pagination, Keyboard]}
          spaceBetween={0}
          slidesPerView={1}
          grabCursor
          mousewheel
          keyboard={{ enabled: true, onlyInViewport: true }}
        >
          <StatusChip
            listingStatus={listingStatus?.listingStatus ?? "Unknown"}
          />
          {photos.map((src, idx) => (
            <SwiperSlide key={idx}>
              <SwiperImageContainer>
                <Image src={src} alt={`Property photo ${idx + 1}`} />
              </SwiperImageContainer>
            </SwiperSlide>
          ))}
          {photos.length === 0 && (
            <SwiperSlide>
              <SwiperImageContainer>
                <ImageUnavailable message="No images available" />
              </SwiperImageContainer>
            </SwiperSlide>
          )}
        </Swiper>

        <Box px={2} mt={2}>
          <Typography variant="h5">{fullAddress}</Typography>
        </Box>
        <Box p={2}>
          <PropertyInfoSection container>
            <Grid container item spacing={1}>
              <Grid item xs={4}>
                <Typography variant="body2" gutterBottom>
                  Listing Price
                </Typography>
                <Typography variant="h6">
                  {textFormatter.currency(
                    getListingCurrentPrice({
                      status: listingStatus?.listingStatus,
                      soldPrice: soldPrice,
                      listPrice: listPrice,
                    }),
                  )}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" gutterBottom>
                  Beds
                </Typography>
                <Typography variant="h6">
                  {textFormatter.number(bedroomCount ?? numBedrooms)}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" gutterBottom>
                  Baths
                </Typography>
                <Typography variant="h6">
                  {textFormatter.number(bathroomCount ?? numBathrooms)}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" gutterBottom>
                  Living Area
                </Typography>
                <Typography variant="h6">
                  {textFormatter.area(internalSize)}
                </Typography>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            <Grid container item spacing={1}>
              <Grid item xs={6}>
                <Typography variant="body2" gutterBottom>
                  Neighborhood
                </Typography>
                <Typography gutterBottom>{neighborhood || "N/A"}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" gutterBottom>
                  Year Built
                </Typography>
                <Typography gutterBottom>{yearBuilt || "N/A"}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" gutterBottom>
                  Property Type
                </Typography>
                <Typography gutterBottom>
                  {propertyType?.propertyType || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" gutterBottom>
                  Lot Size
                </Typography>
                <Typography gutterBottom>
                  {lotSize ? textFormatter.area(lotSize) : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" gutterBottom>
                  Parking Spaces
                </Typography>
                <Typography gutterBottom>
                  {textFormatter.number(parkingGarageSpaces)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" gutterBottom>
                  Transaction Type
                </Typography>
                <Typography gutterBottom>
                  {textFormatter.transactionType(isLease)}
                </Typography>
              </Grid>
            </Grid>
          </PropertyInfoSection>
        </Box>

        {description && (
          <>
            <Box px={2} mt={2}>
              <Typography variant="h5">Description</Typography>
            </Box>
            <Box p={2}>
              <PropertyInfoSection container>
                <Typography>
                  {textFormatter.valueToText(description)}
                </Typography>
              </PropertyInfoSection>
            </Box>
          </>
        )}

        <Box px={2} mb={2}>
          <Button
            variant="contained"
            color="primary"
            component={MuiLink}
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            endIcon={<OpenInNewIcon />}
          >
            View Full Listing
          </Button>
        </Box>
      </Box>
    </ChatWidgetContainer>
  );
};
